import { Cell, Grid, PagedTable } from 'bold-ui'
import { ChartMonthSelector, DateRange } from 'bold-ui/lib/components/Chart'
import { TableBox } from 'components/table/TableBox'
import { usePagedTableProps } from 'components/table/usePagedTableProps'
import { PageParams } from 'graphql/types.generated'
import moment from 'moment'
import { useState } from 'react'
import { tipoGlicemiaRecord } from 'types/enums'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { PeriodoGestacaoModel } from '../types/PeriodoGestacaoModel'
import { GraficoGlicemiaView } from './graficos/glicemia/GraficoGlicemiaView'
import { renderDataHoraMedicao, renderMedicao } from './render-medicoes'
import { getMedicoesComGlicemia } from './util-medicoes'

export interface HistoricoMedicoesGlicemiaViewProps {
  medicoes: ReadonlyArray<HistoricoMedicaoModel>
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
}

export function HistoricoMedicoesGlicemiaView(props: HistoricoMedicoesGlicemiaViewProps) {
  const { medicoes, gestacoes } = props

  const [tableState, setTableState] = useState<{ pageParams?: PageParams }>({ pageParams: { page: 0, size: 5 } })
  const [dateRange, setDateRange] = useState<DateRange>({
    init: moment().startOf('month').startOf('day').toDate(),
    end: moment().endOf('month').add(1, 'millisecond').subtract(1, 'day').toDate(),
  })

  const medicoesComGlicemia = getMedicoesComGlicemia(medicoes)
  const { page: currentPage, size: itensPerPage } = tableState.pageParams
  const totalElements = medicoesComGlicemia?.length ?? 0
  const totalPages = Math.ceil(totalElements / itensPerPage)
  const content = medicoesComGlicemia?.slice(currentPage * itensPerPage, (currentPage + 1) * itensPerPage) ?? []
  const medicoesPage = {
    content: content,
    pageInfo: {
      number: currentPage,
      size: itensPerPage,
      totalPages: totalPages,
      totalElements: medicoesComGlicemia.length,
      first: currentPage === 0,
      last: currentPage === totalPages - 1,
      numberOfElements: content.length,
    },
  }

  const tableProps = usePagedTableProps({
    loading: false,
    onChange: setTableState,
    result: medicoesPage,
  })

  return (
    <Grid>
      <Cell size={12}>
        <TableBox>
          <PagedTable<HistoricoMedicaoModel>
            {...tableProps}
            columns={[
              {
                header: 'Data da medição',
                name: 'dataMedicao',
                render: renderDataHoraMedicao,
              },
              {
                header: 'Glicemia capilar (mg/dL)',
                name: 'valorGlicemia',
                render: renderMedicao((med) => ({
                  medicao: med?.valorGlicemia ? med?.valorGlicemia : '-',
                })),
              },
              {
                header: 'Momento da coleta',
                name: 'tipoGlicemia',
                render: renderMedicao((med) => ({
                  medicao: med?.tipoGlicemia ? tipoGlicemiaRecord[med.tipoGlicemia] : '-',
                })),
              },
            ]}
          />
        </TableBox>
      </Cell>

      <Cell size={12}>
        <ChartMonthSelector onChange={setDateRange} />
      </Cell>

      <Cell size={12}>
        <GraficoGlicemiaView {...{ medicoesComGlicemia, dateRange, gestacoes }} />
      </Cell>
    </Grid>
  )
}
